// Crew / Plant / Vehicles / Maintenance — one row per resource, days across
// the top, in ZT's board shape (round 22).
//
// Fixed 150px day columns (twice the gantt's — a resource's bar carries a job
// title, a job's bar only a name) over the same anchor-independent window,
// auto-scrolled to centre today. The name column's width is COMPUTED from the
// longest name and id-line rather than fixed, because "which excavator" is
// the whole point of this column and a truncated plate reads as a missing
// one. Every resource of the kind is listed even with no bookings — seeing
// who is FREE is half of what the board answers.
//
// Kept Dayboard extras, deliberately: the away hatch on day cells (per-
// resource unavailability is first-class here), click-an-empty-day to book
// that resource, and red conflict bars.
//
// Unavailability is drawn here as a first-class band rather than a footnote —
// a resource that is away, serviced or retired reads as unavailable on its
// own row, in the same place you look for its work.
import { useEffect, useMemo, useRef } from 'react';
import {
  eachDate,
  compactName,
  formatClockRange,
  monthLabel,
  todayISO,
  weekdayShort,
  type DateOnly,
} from '../../lib/dates';
import { jobColour } from '../../lib/jobColour';
import { nwShade, daySegments, type NwLookup, type WorksOn } from '../../lib/nonWorkingDays';
import { isCrewKind } from '../../lib/resourceGroups';
import { RepeatingLabel } from './RepeatingLabel';
import type { CalendarBooking, Resource, Unavailability } from '../../schedulingApi';

const DAY_W = 150;
const LANE_H = 34;
const BAR_H = 26;

/**
 * Pack bookings into the fewest lanes with no overlap. Touching boundaries
 * are explicitly not a clash in this product, so back-to-back bookings may
 * share a lane; genuine overlap (flagged conflicts waiting to be re-planned)
 * stacks.
 */
function packLanes(bookings: CalendarBooking[]): Map<string, number> {
  const lanes: { end: DateOnly }[] = [];
  const assigned = new Map<string, number>();

  for (const booking of [...bookings].sort(
    (a, b) => a.startDate.localeCompare(b.startDate) || b.endDate.localeCompare(a.endDate)
  )) {
    let lane = lanes.findIndex((l) => l.end < booking.startDate);
    if (lane === -1) {
      lanes.push({ end: booking.endDate });
      lane = lanes.length - 1;
    } else {
      lanes[lane] = { end: booking.endDate };
    }
    assigned.set(booking.id, lane);
  }
  return assigned;
}

export function ResourceTimeline({
  resources,
  bookings,
  away,
  days,
  onSelectBooking,
  onAddOn,
  nwInfo,
  worksOn,
}: {
  resources: Resource[];
  bookings: CalendarBooking[];
  away: Unavailability[];
  days: DateOnly[];
  onSelectBooking: (booking: CalendarBooking) => void;
  onAddOn: (date: DateOnly, resourceId: string) => void;
  nwInfo: NwLookup;
  worksOn: WorksOn;
}) {
  const today = todayISO();
  const from = days[0]!;
  const to = days[days.length - 1]!;
  const offsets = useMemo(() => new Map(days.map((d, i) => [d, i])), [days]);

  // The id line: "WC-01 · XT17AZ". Both optional, mono, under the name.
  const idLine = (r: Resource) =>
    [r.identifier, r.registrationNumber].filter(Boolean).join(' · ');

  // Wide enough for the longest name AND the longest id line (≈7.5px and 7px
  // per character at their sizes), floored at 120px.
  const nameW = useMemo(
    () =>
      Math.max(
        120,
        ...resources.map((r) =>
          Math.max(
            (isCrewKind(r.kind) ? compactName(r.name) : r.name).length * 7.5,
            idLine(r).length * 7
          )
        )
      ) + 24,
    [resources]
  );

  const totalW = nameW + days.length * DAY_W;

  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    const todayOffset = offsets.get(today);
    if (!el || todayOffset === undefined) return;
    el.scrollLeft = nameW + todayOffset * DAY_W - el.clientWidth / 2 + DAY_W / 2;
  }, [offsets, today, nameW]);

  if (resources.length === 0) {
    return (
      <div className="card p-10 text-center">
        <p className="text-sm font-medium text-gray-900">Nothing to show on this board</p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500">
          Add a resource of this kind, or clear the filters above.
        </p>
      </div>
    );
  }

  const isMonday = (date: DateOnly) => new Date(`${date}T00:00:00`).getDay() === 1;

  return (
    <div className="card overflow-hidden">
      <div ref={scrollRef} style={{ overflow: 'auto', maxHeight: '75vh' }}>
        <div style={{ width: totalW, minWidth: '100%' }}>
          {/* Header — a true frozen corner: sticky top AND sticky left. */}
          <div className="sticky top-0 z-10 flex border-b border-gray-200 bg-gray-50">
            <div
              style={{ width: nameW, minWidth: nameW }}
              className="sticky left-0 z-20 flex-none border-r border-gray-200 bg-gray-50 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500"
            >
              Resource
            </div>
            <div className="grid" style={{ gridTemplateColumns: `repeat(${days.length}, ${DAY_W}px)` }}>
              {days.map((date) => {
                const nw = nwInfo(date);
                return (
                  <div
                    key={date}
                    className={`border-r border-gray-200 py-1 text-center ${
                      date === today ? 'bg-brand-100' : nwShade(nw, true)
                    } ${isMonday(date) ? 'border-l-2 border-l-gray-300' : ''}`}
                  >
                    <div className="text-xs font-medium leading-none text-gray-500">
                      {weekdayShort(date)}
                    </div>
                    <div
                      className={`text-xs font-bold leading-tight ${
                        date === today ? 'text-brand-800' : 'text-gray-700'
                      }`}
                    >
                      {Number(date.slice(8, 10))}
                    </div>
                    <div className="text-[10px] leading-none text-gray-400">
                      {monthLabel(date).slice(0, 3)}
                    </div>
                    {nw?.dated && (
                      <div
                        className="mt-0.5 truncate px-0.5 text-[9px] font-medium leading-none text-red-700"
                        title={nw.name ?? undefined}
                      >
                        {nw.name}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {resources.map((resource) => {
            const mine = bookings.filter((b) => b.resourceId === resource.id);
            const lanes = packLanes(mine);
            const laneCount = Math.max(1, ...[...lanes.values()].map((l) => l + 1));
            const rowHeight = laneCount * LANE_H + 8;
            const myAway = away.filter((a) => a.resourceId === resource.id);
            const awayDates = new Set(myAway.flatMap((a) => eachDate(a.startDate, a.endDate)));
            // A resource whose status is not `available` is unavailable for
            // the whole window, not just the dated entries — same first-class
            // treatment, different source.
            const alwaysOff = resource.status !== 'available';
            const displayName = isCrewKind(resource.kind)
              ? compactName(resource.name)
              : resource.name;

            return (
              <div key={resource.id} className="flex border-b border-gray-100">
                <div
                  style={{ width: nameW, minWidth: nameW }}
                  className="sticky left-0 z-[5] flex flex-none flex-col justify-center border-r border-gray-200 bg-white px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="truncate text-sm font-semibold text-gray-800"
                      title={resource.name}
                    >
                      {displayName}
                    </span>
                    {alwaysOff && (
                      <span className="flex-none rounded-full bg-gray-50 px-1.5 py-0.5 text-[0.65rem] font-medium text-gray-700 ring-1 ring-inset ring-gray-500/25">
                        {resource.status === 'archived' ? 'Archived' : 'Unavailable'}
                      </span>
                    )}
                  </div>
                  {idLine(resource) && (
                    <div className="truncate font-mono text-xs text-gray-400">
                      {idLine(resource)}
                    </div>
                  )}
                </div>

                <div className="relative" style={{ width: days.length * DAY_W, height: rowHeight }}>
                  {/* Day cells: shading, away hatch, and click-to-book. */}
                  <div className="absolute inset-0 flex">
                    {days.map((date) => {
                      const off = alwaysOff || awayDates.has(date);
                      const nw = nwInfo(date);
                      return (
                        <button
                          key={date}
                          type="button"
                          onClick={() => onAddOn(date, resource.id)}
                          aria-label={`Book ${resource.name} on ${date}`}
                          style={{ width: DAY_W }}
                          className={`schedule-daycell h-full border-r border-gray-100 ${
                            off
                              ? 'schedule-away'
                              : date === today
                                ? 'bg-brand-50/60'
                                : nwShade(nw) || 'bg-white hover:bg-brand-50/50'
                          } ${isMonday(date) ? 'border-l border-l-gray-200' : ''}`}
                        />
                      );
                    })}
                  </div>

                  {mine.map((booking) => {
                    const lane = lanes.get(booking.id) ?? 0;
                    const colour = jobColour(
                      booking.jobNumber,
                      booking.jobId,
                      booking.inConflict
                    );
                    const isMaint = booking.kind === 'maintenance';
                    const label = isMaint
                      ? `Maintenance${booking.supplierName ? ` · ${booking.supplierName}` : ''}`
                      : (booking.jobTitle ?? '');
                    const times = formatClockRange(booking.startTime, booking.endTime);

                    return daySegments(booking, from, to, worksOn).map((segment) => {
                      const segOffset = offsets.get(segment.start)!;
                      const segSpan = offsets.get(segment.end)! - segOffset + 1;
                      return (
                        <button
                          key={`${booking.id}:${segment.start}`}
                          type="button"
                          onClick={() => onSelectBooking(booking)}
                          title={
                            isMaint
                              ? `Maintenance · ${booking.supplierName ?? ''} · all day${
                                  booking.note ? ` · ${booking.note}` : ''
                                }`
                              : `${booking.jobNumber} · ${booking.jobTitle} · ${times ?? 'all day'}${
                                  booking.note ? ` · ${booking.note}` : ''
                                }${booking.inConflict ? ', needs re-planning' : ''}`
                          }
                          style={{
                            position: 'absolute',
                            left: segOffset * DAY_W,
                            width: segSpan * DAY_W,
                            top: lane * LANE_H + 4,
                            height: BAR_H,
                          }}
                          className={`bar-clip flex items-center rounded text-xs font-semibold transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-brand-600 ${colour.bar} ${colour.barText}`}
                        >
                          <RepeatingLabel
                            width={segSpan * DAY_W}
                            labelWidth={(label.length + (isMaint ? 0 : 8)) * 6.8}
                            stickyLeft={nameW}
                          >
                            <span className="font-semibold">{label}</span>
                            {!isMaint && (
                              <span className="ml-1 hidden font-normal opacity-80 sm:inline">
                                {booking.jobNumber}
                              </span>
                            )}
                          </RepeatingLabel>
                        </button>
                      );
                    });
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
